import { sub } from 'date-fns'
import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { Cog6ToothIcon, EllipsisVerticalIcon, NewspaperIcon } from '@heroicons/react/24/outline'
import {
    AccountHealthSignal,
    AccountRecord,
    DmFilter,
    Maybe,
    SocialMediaServiceEnum,
    useGetSummaryForAccountsQuery
} from '../api/client'
import {
    ACCOUNTS_QUERY_PARAM,
    encodeIdsQueryParam,
    MODERATION_QUERY_PARAM
} from '../models/QueryParameters'
import { fetchTimePeriodState } from '../store/UIStore'
import { faIconName } from '../util/classNames'
import DashboardButton from './account/DashboardButton'
import { fetchContentExplanation } from './account/fetchContent'
import AccountAuthorisationStatus from './authorise/MetaAccountAuthorisationStatus'
import HealthPill from './account/HealthPill'
import AuthoriseButton from './authorise/AuthoriseButton'
import AutoFetchContentToggle from './toggles/AutoFetchContentToggle'
import ScrapingToggle from './toggles/ScrapingToggle'
import TwitterActivityApiToggle from './toggles/TwitterActivityApiToggle'
import MetaAccountSubscriptionStatus from './authorise/MetaAccountSubscriptionStatus'
import DeleteAccountModal from './DeleteAccountModal'
import { ROW_ACTION_ICON_CLASSES } from './table/rowActions'

type Props = {
    account: AccountRecord
    admin?: boolean
    showAdminFeatures?: boolean
    /**
     * This profile's monitoring health, when the list has fetched it.
     * Passed in rather than fetched here: one query for the page beats one
     * per row.
     */
    monitoringHealth?: AccountHealthSignal
}

type ModerationTone = 'safe' | 'suspect' | 'severe'

/* Written out as full literal strings so Tailwind's JIT scanner picks them up. */
const PILL_TONE_CLASSES: Record<ModerationTone, string> = {
    safe: 'bg-safe-50 text-safe-700',
    suspect: 'bg-suspect-50 text-suspect-700',
    severe: 'bg-severe-50 text-severe-700'
}
const PILL_DOT_CLASSES: Record<ModerationTone, string> = {
    safe: 'bg-safe-600',
    suspect: 'bg-suspect-600',
    severe: 'bg-severe-600'
}
const COUNT_TONE_CLASSES: Record<ModerationTone, string> = {
    safe: 'text-safe',
    suspect: 'text-suspect',
    severe: 'text-severe'
}
const PILL_LABELS: Record<ModerationTone, string> = {
    safe: 'Safe',
    suspect: 'Suspect',
    severe: 'Severe'
}

/* An em dash rather than a blank, so the column still aligns and the absence
   reads as "no data" instead of as a rendering slip. — Claude */
const UNKNOWN_COUNT = '—'

/** `null` = unknown (the server gave up on the query); a real zero stays '0'. */
const formatCount = (count?: Maybe<number>): string | null => (count == null ? null : `${count}`)

/* Unknown and zero both mean "nothing here to act on", so both are muted; only
   the text differs. — Claude */
const isMutedCount = (count: string | null) => count === null || count === '' || count === '0'

/* The dash needs an explanation somewhere, and the link tooltip is the only
   affordance these counts have. — Claude */
const countTitle = (moderation: ModerationTone, count: string | null) =>
    count === null
        ? 'Comment counts are unavailable for this period'
        : `View ${PILL_LABELS[moderation].toLowerCase()} comments`

function AccountRow(props: Props) {
    const { account, admin, showAdminFeatures, monitoringHealth } = props
    const navigate = useNavigate()
    // SOMEDAY replace this with DateTimePickerContainer.tsx
    const fetchTimePeriodMinutes = useRecoilValue(fetchTimePeriodState)

    /* Derived with useMemo, not state + a mount effect: the effect ran once on
       mount and re-minted both dates a few milliseconds after the render that
       had already started the summary query, so Apollo saw changed variables,
       aborted the in-flight request and fired it again — two requests per row
       on every homescreen load. Deriving keeps the dates stable for the life of
       the mount and still recomputes when the time-period picker changes, which
       is all the effect was for. Both bounds come from the same `now` so the
       window is exactly the picked period. — Claude */
    const { startDateTime, endDateTime } = useMemo(() => {
        const now = new Date()
        return {
            startDateTime: sub(now, { minutes: fetchTimePeriodMinutes * 60 * 24 }),
            endDateTime: now
        }
    }, [fetchTimePeriodMinutes])

    const { data, loading } = useGetSummaryForAccountsQuery({
        variables: {
            params: {
                accountIds: [account.id],
                authorProfileIds: [],
                teamIds: [],
                since: startDateTime,
                before: endDateTime,
                socialMediaServiceIds: [],
                excludeOwnComments: true,
                dmFilter: DmFilter.CommentsOnly,
                owned: true
            }
        }
    })

    /* null means the count is unknown — the summary query ran out of its budget
       server-side — and that has to stay distinct from a real zero. "0 severe"
       is a claim about this profile; a dash admits we don't know. Each bucket is
       read independently rather than all three hanging off whether countSafe
       arrived, so one missing value can't silently zero the other two. — Claude */
    const countSafe = formatCount(data?.getSummaryForAccounts?.countSafe)
    const countSuspect = formatCount(data?.getSummaryForAccounts?.countSuspect)
    const countSevere = formatCount(data?.getSummaryForAccounts?.countSevere)

    function moderationLink(moderation: string) {
        return `/account/${account.id}/?${MODERATION_QUERY_PARAM}=${moderation}&profile=${account.id}`
    }

    /* The count itself is the link ("View all" is gone); zeros are muted so a
       scan down the column picks out the profiles that need attention. An
       unknown count is muted for the same reason — it isn't an alert — but reads
       as a dash rather than a number. */
    function generateStatDisplay(moderation: ModerationTone, count: string | null) {
        return (
            <td className="hidden whitespace-nowrap px-4 py-2 text-center sm:table-cell">
                <Link
                    to={moderationLink(moderation)}
                    title={countTitle(moderation, count)}
                    className={`text-sm tabular-nums hover:underline ${
                        isMutedCount(count)
                            ? 'font-medium text-ink-300'
                            : `font-semibold ${COUNT_TONE_CLASSES[moderation]}`
                    }`}
                >
                    {count ?? UNKNOWN_COUNT}
                </Link>
            </td>
        )
    }

    function generateStatPill(moderation: ModerationTone, count: string | null) {
        return (
            <Link
                to={moderationLink(moderation)}
                title={countTitle(moderation, count)}
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
                    isMutedCount(count) ? 'bg-ink-100 text-ink-400' : PILL_TONE_CLASSES[moderation]
                }`}
            >
                <span
                    className={`h-1.5 w-1.5 rounded-full ${
                        isMutedCount(count) ? 'bg-ink-300' : PILL_DOT_CLASSES[moderation]
                    }`}
                    aria-hidden="true"
                ></span>
                {PILL_LABELS[moderation]} {count ?? UNKNOWN_COUNT}
            </Link>
        )
    }

    const fetchContentHere = admin
        ? fetchContentExplanation(account.socialMediaServiceShortName)
        : undefined

    let showTwitterActivityApiToggle =
        admin &&
        account.hasAuthorisation &&
        account.socialMediaServiceShortName &&
        account.socialMediaServiceShortName === 'twitter'

    let showScrapingToggle =
        admin &&
        account.socialMediaServiceShortName &&
        account.socialMediaServiceShortName === 'twitter'

    let isMeta =
        account.socialMediaServiceShortName?.toLowerCase() ===
            SocialMediaServiceEnum.Instagram.toLowerCase() ||
        account.socialMediaServiceShortName?.toLowerCase() ===
            SocialMediaServiceEnum.Facebook.toLowerCase()

    const [deleteModalVisible, setDeleteModalVisible] = useState(false)
    const [deleted, setDeleted] = useState(false)
    const [adminExpanded, setAdminExpanded] = useState(false)

    let name = account.serviceAccountUsername?.startsWith('u_')
        ? 'reddit'
        : account.socialMediaServiceShortName || ''
    let iconName = faIconName(name, false)

    const settingsPath = `/account/${account.id}/settings`

    const showAdminRow = admin && showAdminFeatures

    // The whole row is a shortcut to the profile dashboard (same destination as
    // the dashboard icon button). Clicks that land on the row's own interactive
    // controls — auth/dashboard/settings/admin buttons and the stat links — keep
    // their own destinations, so we only navigate when the click misses those.
    // This is a mouse convenience; keyboard/AT users still use the dashboard
    // button, which points at the same place.
    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
        if ((event.target as HTMLElement).closest('a, button, input, label, [role="switch"]')) {
            return
        }
        navigate(`/account/${account.id}`)
    }

    return (
        <React.Fragment>
            <tr
                className="cursor-pointer hover:bg-ink-50"
                key={account.id}
                onClick={handleRowClick}
            >
                <td className="px-4 py-2">
                    <div className="flex items-center gap-3">
                        <div
                            className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-ink-100 text-ink-800"
                            title={account.socialMediaServiceShortName || ''}
                        >
                            <i className={`fab ${iconName} text-sm`}></i>
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 text-sm font-medium text-ink-900">
                                <span className="truncate">{account.accountName}</span>
                                <AuthoriseButton account={account} onlyWhenUnauthorised />
                                {/* Is anything actually collecting this profile's comments?
                                    Shown for any platform, since monitoring is not Meta-only,
                                    and driven by a signal the list fetched once for the whole
                                    page rather than a query per row. */}
                                {showAdminRow && monitoringHealth && (
                                    <span className="flex flex-none items-center font-normal">
                                        <HealthPill
                                            label={monitoringHealth.label}
                                            status={monitoringHealth.status}
                                            detail={monitoringHealth.detail}
                                        />
                                    </span>
                                )}
                                {/* At-a-glance Meta auth/subscription health sits on the name
                                    line so broken profiles stand out without changing the row
                                    height; full labelled detail with fix links lives in the
                                    admin row behind the kebab.

                                    Only for AUTHORISED profiles. On an unauthorised one there is
                                    no token to check and no subscription to have, so both checks
                                    reported failure — a red cross for the normal, known state of
                                    a monitored profile, sitting beside the Authorisation link
                                    that already says so. It also spent two Meta API calls per row
                                    to learn nothing. */}
                                {showAdminRow && isMeta && account.hasAuthorisation && (
                                    <span className="flex flex-none items-center gap-2 font-normal">
                                        <AccountAuthorisationStatus account={account} compact />
                                        <MetaAccountSubscriptionStatus account={account} compact />
                                    </span>
                                )}
                            </div>
                            <div className="truncate text-xs text-ink-500">
                                {account.serviceAccountUsername}
                            </div>
                            {/* Below sm the count columns are hidden and collapse into
                                labelled pills here, so the table never scrolls sideways. */}
                            <div className="mt-1.5 flex flex-wrap gap-1.5 sm:hidden">
                                {loading ? (
                                    <div className="h-5 w-40 animate-pulse rounded-full bg-ink-100"></div>
                                ) : (
                                    <>
                                        {generateStatPill('safe', countSafe)}
                                        {generateStatPill('suspect', countSuspect)}
                                        {generateStatPill('severe', countSevere)}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </td>

                {loading ? (
                    <>
                        {['safe', 'suspect', 'severe'].map((moderation) => (
                            <td key={moderation} className="hidden px-4 py-2 sm:table-cell">
                                <div className="mx-auto h-4 w-8 animate-pulse rounded bg-ink-100"></div>
                            </td>
                        ))}
                    </>
                ) : (
                    <>
                        {generateStatDisplay('safe', countSafe)}
                        {generateStatDisplay('suspect', countSuspect)}
                        {generateStatDisplay('severe', countSevere)}
                    </>
                )}

                <td className="whitespace-nowrap px-4 py-2">
                    <div className="flex items-center justify-end gap-1">
                        <DashboardButton accountId={account.id} variant="icon" />
                        {/* Admin-only because the post list lives under /admin. */}
                        {admin && (
                            <Link
                                to={`/admin/post?${ACCOUNTS_QUERY_PARAM}=${encodeIdsQueryParam([account.id])}`}
                                title="View posts"
                                aria-label="View posts"
                                className={ROW_ACTION_ICON_CLASSES}
                                data-cy="account_view_posts_button"
                            >
                                <NewspaperIcon className="h-5 w-5" aria-hidden="true" />
                            </Link>
                        )}
                        <Link
                            to={settingsPath}
                            title="Profile settings"
                            aria-label="Profile settings"
                            className={ROW_ACTION_ICON_CLASSES}
                            data-cy="account_settings_button"
                        >
                            <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />
                        </Link>
                        {showAdminRow && (
                            <button
                                type="button"
                                onClick={() => setAdminExpanded(!adminExpanded)}
                                title="Admin actions"
                                aria-label="Admin actions"
                                aria-expanded={adminExpanded}
                                className={`${ROW_ACTION_ICON_CLASSES} ${
                                    adminExpanded ? 'border-ink-200 bg-ink-100' : ''
                                }`}
                            >
                                <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                </td>
            </tr>

            {/* Admin extras live in an expandable row behind the kebab instead of
                four permanent columns, keeping the default table compact. */}
            {showAdminRow && adminExpanded && (
                <tr className="bg-ink-50">
                    <td colSpan={5} className="px-4 py-3">
                        <div className="flex flex-wrap items-start gap-x-10 gap-y-3">
                            {(fetchContentHere ||
                                showTwitterActivityApiToggle ||
                                showScrapingToggle) && (
                                <div className="flex flex-col gap-2 text-xs">
                                    {fetchContentHere ? (
                                        <AutoFetchContentToggle
                                            account={account}
                                            size="small"
                                            tooltip={fetchContentHere}
                                        />
                                    ) : null}
                                    {showTwitterActivityApiToggle ? (
                                        <TwitterActivityApiToggle account={account} />
                                    ) : null}
                                    {showScrapingToggle ? (
                                        <ScrapingToggle account={account} size="small" />
                                    ) : null}
                                </div>
                            )}
                            <div className="text-xs text-ink-500">
                                {account.teamName && <div>Team: {account.teamName}</div>}
                                <div>Owned by: {account.ownedBy}</div>
                                <div>Created on: {new Date(account.createdAt).toDateString()}</div>
                                {account.instagramAuthMethod && (
                                    <div>Auth method: {account.instagramAuthMethod}</div>
                                )}
                            </div>
                            {isMeta && (
                                <div className="text-xs">
                                    <AccountAuthorisationStatus account={account} />
                                    <MetaAccountSubscriptionStatus account={account} />
                                </div>
                            )}
                            <div className="ml-auto">
                                <button
                                    type="button"
                                    onClick={() => setDeleteModalVisible(true)}
                                    disabled={deleted}
                                    className={`inline-flex items-center rounded-md border border-transparent px-3 py-1.5 text-xs ${
                                        deleted
                                            ? 'text-ink-400 cursor-not-allowed'
                                            : 'text-arwen-600 hover:bg-arwen-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500'
                                    }`}
                                >
                                    {deleted ? 'Deleted' : 'Delete'}
                                </button>
                                {deleteModalVisible && (
                                    <DeleteAccountModal
                                        setVisible={setDeleteModalVisible}
                                        accountId={account.id}
                                        onDeleted={() => setDeleted(true)}
                                    />
                                )}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </React.Fragment>
    )
}

export default AccountRow
